<template>
  <div class="filter-row" :class="{ 'is-open': valuesOpen }">
    <b-dropdown
      size="sm"
      variant="outline-secondary"
      class="filter-row__field"
      :disabled="!availableFields.length"
      no-caret
    >
      <template #button-content>
        {{ field.label }}
        <font-awesome-icon v-if="availableFields.length" :icon="['fal', 'chevron-down']" class="ml-1" />
      </template>
      <b-dropdown-item
        v-for="f in availableFields"
        :key="f.key"
        :data-test="`switch-field-${f.key}`"
        @click="$emit('update', { key: f.key, values: [] })"
      >
        {{ f.label }}
      </b-dropdown-item>
    </b-dropdown>

    <span data-test="operator" class="filter-row__operator">{{ operatorLabel(row.values.length) }}</span>

    <b-dropdown
      ref="valuesDropdown"
      variant="link"
      class="filter-row__values"
      toggle-class="filter-row__values-toggle"
      :toggle-attrs="{ 'aria-label': `${field.label} values` }"
      menu-class="filter-row__menu"
      no-caret
      block
      @show="checklistKey += 1"
      @shown="valuesOpen = true"
      @hidden="valuesOpen = false"
    >
      <template #button-content>
        <span class="filter-row__chips">
          <span
            v-for="v in row.values"
            :key="v"
            data-test="value-chip"
            class="filter-row__chip"
            :class="{ 'is-stale': staleValues.includes(v) }"
          >
            <font-awesome-icon
              v-if="staleValues.includes(v)"
              :icon="['fal', 'exclamation-triangle']"
              class="filter-row__chip-warning"
            />
            {{ labelFor(v) }}
            <span v-if="countFor(v) !== null" class="filter-row__chip-count">{{ toNumber(countFor(v)) }}</span>
            <span
              role="button"
              tabindex="0"
              class="filter-row__chip-remove"
              :aria-label="`Remove ${labelFor(v)}`"
              @click.stop.prevent="removeValue(v)"
              @keydown.enter.stop.prevent="removeValue(v)"
              @keydown.space.stop.prevent="removeValue(v)"
            >
              <font-awesome-icon :icon="['fal', 'times']" />
            </span>
          </span>
          <span v-if="!row.values.length" class="filter-row__placeholder">Choose {{ field.label.toLowerCase() }}</span>
        </span>
        <font-awesome-icon :icon="['fal', 'chevron-down']" class="filter-row__chevron" />
      </template>
      <b-dropdown-form form-class="p-0" @submit.prevent.stop>
        <audience-values-checklist
          :key="checklistKey"
          :field="field"
          :value="row.values"
          :stale-values="staleValues"
          @done="onDone"
          @cancel="closeValues"
        />
      </b-dropdown-form>
    </b-dropdown>

    <b-button
      variant="link"
      class="filter-row__remove"
      :aria-label="`Remove ${field.label} filter`"
      @click="$emit('remove')"
    >
      <font-awesome-icon :icon="['fal', 'times']" />
    </b-button>
  </div>
</template>

<script>
import { nextTick, onMounted, ref } from 'vue';
import AudienceValuesChecklist from '@/pages/recognition/components/audience/AudienceValuesChecklist.vue';
import { operatorLabel, valueLabel } from '@/pages/recognition/utils/audience';
import { toNumber } from '@/utils/common';

export default {
  name: 'AudienceFilterRow',
  components: { AudienceValuesChecklist },
  props: {
    row: { type: Object, required: true },
    field: { type: Object, required: true },
    availableFields: { type: Array, default: () => [] },
    staleValues: { type: Array, default: () => [] },
    openOnMount: { type: Boolean, default: false },
  },
  emits: ['update', 'remove'],
  setup(props, { emit }) {
    const valuesDropdown = ref(null);
    const valuesOpen = ref(false);
    // Remount on every open so cancelled ticks and searches don't survive.
    const checklistKey = ref(0);

    const labelFor = v => valueLabel(props.row.key, v, [props.field]);
    const countFor = v => props.field.values?.find(o => String(o.value) === String(v))?.count ?? null;

    const closeValues = () => valuesDropdown.value?.hide(true);

    onMounted(async () => {
      if (props.openOnMount) {
        await nextTick();
        valuesDropdown.value?.show();
      }
    });

    const removeValue = value =>
      emit('update', { key: props.row.key, values: props.row.values.filter(v => v !== value) });

    const onDone = values => {
      emit('update', { key: props.row.key, values });
      closeValues();
    };

    return {
      valuesDropdown,
      valuesOpen,
      checklistKey,
      labelFor,
      countFor,
      closeValues,
      onDone,
      removeValue,
      toNumber,
      operatorLabel,
    };
  },
};
</script>

<style scoped lang="scss">
.filter-row {
  align-items: center;
  background: var(--palette-color-base-white);
  border: 1px solid var(--palette-color-default-lighten-80);
  border-radius: 0.75rem;
  display: flex;
  gap: 0.75rem;
  padding: 0.625rem 0.5rem 0.625rem 1rem;

  &.is-open {
    border-color: var(--palette-color-brand-primary);
    box-shadow: 0 0 0 1px var(--palette-color-brand-primary);
  }
}

.filter-row__operator {
  color: var(--palette-color-default-lighten-20);
  flex-shrink: 0;
  font-size: 0.875rem;
}

.filter-row__values {
  flex: 1;
  min-width: 0;

  ::v-deep .filter-row__values-toggle {
    align-items: center;
    border: 1px solid var(--palette-color-default-lighten-80);
    border-radius: 0.5rem;
    color: var(--palette-color-default);
    display: flex;
    gap: 0.5rem;
    justify-content: space-between;
    padding: 0.375rem 0.625rem;
    text-decoration: none;
  }

  ::v-deep .filter-row__menu {
    border-radius: 1rem;
    box-shadow: 0 12px 32px rgba(41, 40, 36, 0.14);
    overflow: hidden;
    padding: 0;
  }
}

.filter-row__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  min-width: 0;
}

.filter-row__chip {
  background: var(--palette-color-core-1-lighten-70);
  border-radius: 999px;
  color: var(--palette-color-brand-primary-darken-10);
  font-size: 0.8125rem;
  font-weight: 600;
  padding: 0.125rem 0.625rem;

  &.is-stale {
    text-decoration: line-through;
  }
}

.filter-row__chip-remove {
  border-radius: 999px;
  color: var(--palette-color-brand-primary-darken-10);
  cursor: pointer;
  margin-left: 0.25rem;
  padding: 0 0.25rem;

  &:hover,
  &:focus-visible {
    background: var(--palette-color-core-1-lighten-60);
  }
}

.filter-row__chip-count {
  color: var(--palette-color-default-lighten-20);
  font-weight: 500;
  margin-left: 0.25rem;
}

.filter-row__chip-warning {
  color: var(--palette-color-warning);
  margin-right: 0.25rem;
}

.filter-row__placeholder {
  color: var(--palette-color-default-lighten-30);
  font-size: 0.875rem;
}

.filter-row__chevron {
  color: var(--palette-color-default-lighten-20);
  flex-shrink: 0;
}

.filter-row__remove {
  color: var(--palette-color-default-lighten-20);
  flex-shrink: 0;
}
</style>
